import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { useAppSelector } from './state/hooks';
import { actionCreators } from './state';
import { bindActionCreators } from 'redux';
import * as ERV_API from './services/ervSalesGate';
import BookingScreen from './screens/BookingScreen';
import { buildTrackingData, TrackingData } from './services/tracking';
import { useTracking } from 'react-tracking';
import Helper from './settings/helperFunctions';

declare global {
  interface Window {
    appEventData: any;
  }
}

const App = () => {
  const dispatch = useDispatch();

  const { Track, trackEvent } = useTracking(
    {},
    {
      dispatch: (data: TrackingData) => {
        let t = buildTrackingData(data);
        if (window.DEBUG) {
          console.log('%c Tracking', 'background: #faffda; color: #ff5353');
          console.dir(t);
          console.dir(window.appEventData);
          console.log('%c Tracking end', 'background: #faffda; color: #ff5353');
        }
        ((window as any).appEventData = (window as any).appEventData || []).push(t);
      },
    },
  );
  const { sessionId, appName } = useAppSelector((state) => state.settingsReducer);
  const { properties, components } = useAppSelector((state) => state.configReducer);
  const {
    setConfigFile,
    setEnvironment,
    setSessionId,
    setAppName,
    setPartnerId,
    setTipmode,
    setAgent,
    setExternalReference,
    setOenr1,
    setWayOfAccess,
    setSkin,
    setLanguage,
    setConfirmationTarget,
    setConfirmationType,
    setRebound,
    setErrorType,
    setAgencyInformation,
    setCurrentDate,
    setPromptAgent,
    setCurrentSteps,
  } = bindActionCreators(actionCreators, dispatch);

  const [skinLoaded, setSkinLoaded] = useState<boolean>(false);

  useEffect(() => {
    // custom tracking for InfoBoxHtml component
    const listener = (e: any) => {
      trackEvent(e.detail);
    };
    document.addEventListener('trackEvent', listener);

    // SessionId
    let sessionId = uuidv4();
    setSessionId(sessionId);

    // AppName and Environment
    let environment: Stage = 'DEV';
    let appName: string = process.env.REACT_APP_DEFAULT_APP
      ? process.env.REACT_APP_DEFAULT_APP
      : 'ba-standard';
    if (typeof ervAppRuntime === 'object') {
      environment = 'PROD';
      if (typeof ervAppRuntime.getStage === 'function') {
        environment = ervAppRuntime.getStage()!;
      }
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().appName === 'string'
      ) {
        appName = ervAppRuntime.getRuntimeConfig().appName!;
      }
    }
    appName = Helper.appRedirects(appName);
    setEnvironment(environment);
    if (window.DEBUG) console.log('Environment: ' + environment);

    // PartnerID
    let partnerId: string = '040011000159';

    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().partnerId === 'string'
      )
        partnerId = ervAppRuntime.getRuntimeConfig().partnerId!;
    }
    setPartnerId(partnerId);
    if (window.DEBUG) console.log('PartnerId: ' + partnerId);

    // Tipmode
    let tipmode: boolean = false;
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().tipmode === 'boolean' &&
        ervAppRuntime.getRuntimeConfig().tipmode === true
      ) {
        tipmode = true;
        setTipmode(true);
        if (window.DEBUG) console.log('Tipmode: ' + tipmode);
      }
    }

    // Language
    let language: LanguageType = 'de';
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().language === 'string'
      ) {
        if (ervAppRuntime.getRuntimeConfig().language === 'en') language = 'de';
        if (window.DEBUG) console.log('Language EN is not supported: ' + language);
      }
    }
    setLanguage(language);

    // Agent
    let agent: string = '';
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().agent === 'string' &&
        ervAppRuntime.getRuntimeConfig().agent
      ) {
        agent = ervAppRuntime.getRuntimeConfig().agent!;
        setAgent(agent);
        if (window.DEBUG) console.log('Agent: ' + agent);
      }
    }

    // externalReference
    // for testing: let externalReference: string = 'blablabla';
    let externalReference: string = '';
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().externalReference === 'string' &&
        ervAppRuntime.getRuntimeConfig().externalReference
      ) {
        externalReference = ervAppRuntime.getRuntimeConfig().externalReference!;
        setExternalReference(externalReference);
        if (window.DEBUG) console.log('externalreference: ' + externalReference);
      }
    }

    // Confirmation
    let confirmationTarget: string = '';
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().confirmationtarget === 'string' &&
        ervAppRuntime.getRuntimeConfig().confirmationtarget
      ) {
        confirmationTarget = ervAppRuntime.getRuntimeConfig().confirmationtarget!;
        setConfirmationTarget(confirmationTarget);
        if (window.DEBUG) console.log('Confirmation target: ' + confirmationTarget);
      }
    }
    let confirmationType: string = '';
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().confirmationtype === 'string' &&
        ervAppRuntime.getRuntimeConfig().confirmationtype
      ) {
        confirmationType = ervAppRuntime.getRuntimeConfig().confirmationtype!;
        setConfirmationType(confirmationType);
        if (window.DEBUG) console.log('Confirmation type: ' + confirmationType);
      }
    }
    let confirmationrebound: Rebound = { keys: [], values: [] };
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().confirmationrebound === 'object' &&
        ervAppRuntime.getRuntimeConfig().confirmationrebound?.keys.length !== 0
      ) {
        confirmationrebound = ervAppRuntime.getRuntimeConfig().confirmationrebound!;
        setRebound(confirmationrebound);
        if (window.DEBUG)
          console.log('Confirmation rebound: ' + JSON.stringify(confirmationrebound));
      }
    }

    // promptagent
    let promptAgent: boolean = false;
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().promptagent === 'string' &&
        ervAppRuntime.getRuntimeConfig().promptagent!.toLowerCase() === 'true'
      ) {
        promptAgent = true;
        if (window.DEBUG) console.log('promptagent: ' + promptAgent);
      }
    }
    setPromptAgent(promptAgent);

    // oenr1
    let oenr1: string = '';
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().oenr1 === 'string' &&
        ervAppRuntime.getRuntimeConfig().oenr1
      ) {
        oenr1 = ervAppRuntime.getRuntimeConfig().oenr1!;
        // remove leading zeros
        let oenr1Number = parseInt(oenr1, 10);
        oenr1 = oenr1Number.toString();

        // add leading zeros to have a 9-digit oenr1
        if (oenr1.length < 9) {
          oenr1 = oenr1.padStart(9, '0');
        }
        setOenr1(oenr1);
        if (window.DEBUG) console.log('Oenr1: ' + oenr1);
      }
    }

    // load skinLink
    let skinLink: string = '';
    if (typeof ervAppRuntime === 'object') {
      if (
        typeof ervAppRuntime.getRuntimeConfig === 'function' &&
        typeof ervAppRuntime.getRuntimeConfig().skinLink === 'string' &&
        ervAppRuntime.getRuntimeConfig().skinLink !== ''
      ) {
        skinLink = ervAppRuntime.getRuntimeConfig().skinLink!;
        if (window.DEBUG) console.log('skinLink: ' + skinLink);
      }
    }

    ERV_API.fetchSkinConfig(skinLink, (responseJSON: SkinState) => {
      if (responseJSON) {
        setSkin(responseJSON);
        if (window.DEBUG) console.log('SkinData: ' + JSON.stringify(responseJSON));
      }
      setSkinLoaded(true);
    });

    const uiParams: Record<string, any> = new URLSearchParams(window.location.search);
    Helper.setUICGIParams(uiParams, dispatch);

    // init application
    if (!Helper.isAppNameCorrect(appName)) {
      setAppName('ba-error');
      setErrorType('BANameFehler');
    } else if (confirmationTarget == '' && confirmationType !== '') {
      setAppName('ba-error');
      setErrorType('ConfirmationTarget');
      ERV_API.logs(
        'BA40\n ConfirmationTarget is not defined.' +
          '\n URL: ' +
          document.location.href +
          '\n Referrer: ' +
          Helper.getReferrertUrl(),
        'BEINFOMAILS',
      );
    } else if (oenr1.length > 9) {
      setAppName('ba-error');
      setErrorType('Oenr1');
    } else if (confirmationType == '' && confirmationTarget !== '') {
      setAppName('ba-error');
      setErrorType('ConfirmationType');
      ERV_API.logs(
        'BA40\n ConfirmationType is not defined.' +
          '\n URL: ' +
          document.location.href +
          '\n Referrer: ' +
          Helper.getReferrertUrl(),
        'BEINFOMAILS',
      );
    } else {
      let data: InitAppRequestBody = {
        parameters: {
          languageCode: 'de',
          currencyCode: 'EUR',
          partnerId: partnerId,
        },
      };
      if (agent !== '') {
        data.parameters.agent = agent;
      }
      if (confirmationTarget && confirmationType) {
        data.parameters.confirmation = {};
        data.parameters.confirmation.target = confirmationTarget;
        data.parameters.confirmation.type = confirmationType;
        if (confirmationrebound && confirmationrebound.keys.length != 0) {
          data.parameters.confirmation.rebound = confirmationrebound;
        }
      }
      ERV_API.initApp(
        sessionId,
        data,
        appName,
        (appName: string, error: null | string, initApp: null | InitAppType) => {
          setAppName(appName);
          if (error) {
            setErrorType(error);
            if (window.DEBUG) console.log('Application initialization Error: ' + error);
          }
          if (window.DEBUG) console.log('Application ' + appName + ' initialized');
          if (initApp?.agencyInfo) setAgencyInformation(initApp.agencyInfo);
          if (initApp?.currentDate) setCurrentDate(initApp?.currentDate);
          if (promptAgent && !initApp?.agencyInfo?.isMecklenburgische) {
            setCurrentSteps([0]);
          }
          if (initApp?.agencyInfo?.isMecklenburgische) {
            setPromptAgent(true);
          }
        },
      );
    }
    return () => {
      document.removeEventListener('trackEvent', listener);
    };
  }, []);

  useEffect(() => {
    if (sessionId != '' && appName != '' && skinLoaded)
      ERV_API.fetchAppConfig(sessionId, (responseJSON: ConfigFile) => {
        if (document.location.href.indexOf('activateSkinConfig') !== -1) {
          if (responseJSON && responseJSON.components) {
            if (
              responseJSON.components[responseJSON.components.length - 1].properties.name !==
              'SkinConfig'
            ) {
              responseJSON.components.push({
                properties: {
                  name: 'SkinConfig',
                  showOnSteps: [
                    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
                  ],
                },
              });
            }
          }
        }
        setConfigFile(responseJSON);
      });
  }, [sessionId, appName, skinLoaded]);

  return (
    <Track>
      <Router>
        <Routes>
          {Object.keys(properties).length > 0 && Object.keys(components).length > 0 && (
            <Route path="/*" element={<BookingScreen />} />
          )}
        </Routes>
      </Router>
    </Track>
  );
};

export default App;
